import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, FormProvider } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";

import { postRequest } from "api/axiosClient";
import { useGetInvoiceComments } from "api/queries/invoice/useGetInvoiceComments";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import Textarea from "components/atoms/Textarea/Textarea";
import Button from "components/atoms/Button/Button";
import Card from "components/molecules/Card/Card";
import CardNew from "components/molecules/Card/CardNew";
import CommentComponentStyles from "constants/CommentComponentStyles";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";

const initialCommentData = [];
const Comments = function ({ invoiceId }) {
  const { REACT_APP_CARGOBASE_BASE_URL } = import.meta.env;
  const methods = useForm();
  const { handleSubmit, getValues, setValue } = methods;
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");

  const [isFieldFocused, setIsFieldFocused] = useState(false);
  const { data: commentData = initialCommentData, isLoading: loadingComment } = useGetInvoiceComments({
    invoiceId,
    options: { enabled: !!invoiceId },
  });

  useEffect(() => {
    const commentsBox = document.getElementById("comments-box");
    if (commentsBox) commentsBox.scrollTop = commentsBox.scrollHeight;
  }, [commentData, isFieldFocused]);

  const postComment = () => {
    setIsFieldFocused(false);
    if (getValues("comment_message")) {
      const payload = {
        comment: getValues("comment_message"),
      };

      setValue("comment_message", "");
      postRequest(`${REACT_APP_CARGOBASE_BASE_URL}/billing/${invoiceId}/comments`, payload)
        .then(() => queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.invoiceCommentsListing(invoiceId) }))
        .catch((err) =>
          errorToast(
            err,
            intl.formatMessage({
              id: "billing.couldNotSubmitComment",
              defaultMessage: "Could not submit comment. Please try again later.",
            }),
          ),
        );
    }
  };

  return (
    <CardNew isLoading={loadingComment} title={<FormattedMessage id="general.comments" defaultMessage="Comments" />}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(postComment)}>
          <Card.Body style={{ maxHeight: "170px", overflowY: "scroll" }} id="comments-box">
            {!isFieldFocused && (
              <>
                {commentData.length > 0 ? (
                  <section className="mb-3 pt-0 br-5">
                    <ul>
                      {commentData.map(({ posted_at, comment, name }, i) => (
                        <li
                          key={`${posted_at}-${name}-${i}`}
                          className={`border-1 border-grey pl-0 pr-0 pb-11 ${
                            commentData.length - 1 === i ? "" : "border-bottom"
                          } ${i === 0 ? "" : "pt-15 "}`}
                        >
                          <section className="d-flex justify-content-between mb-0 font-b3">{comment}</section>
                          <section className="d-flex justify-content-between mb-0 mt-2 font-b3">
                            <span className="text-secondary">{name}</span>
                            <div className="d-flex align-items-center ml-2">
                              <span className="text-secondary">{posted_at}</span>
                            </div>
                          </section>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : (
                  <div className="pb-3">
                    <FormattedMessage id="billing.noComments" defaultMessage="No comments found" />
                  </div>
                )}
              </>
            )}
            <div>
              {isFieldFocused && (
                <div
                  style={{
                    marginBottom: "7px",
                  }}
                  className="font-b8"
                >
                  <FormattedMessage id="general.writeAComment" defaultMessage="Write a comment" />
                </div>
              )}
              <Textarea
                style={{
                  ...CommentComponentStyles,
                  borderRadius: "12px",
                  borderColor: "black",
                  height: isFieldFocused ? "67px" : "24px",
                  lineHeight: isFieldFocused ? "normal" : "12px",
                  marginBottom: isFieldFocused ? "18px" : "",
                }}
                name="comment_message"
                placeholder={
                  user_type === "shipper"
                    ? intl.formatMessage({
                        id: "general.writeAComment",
                        defaultMessage: "Write a comment",
                      })
                    : intl.formatMessage({
                        id: "billing.newComment",
                        defaultMessage: "New comment",
                      })
                }
                rows={isFieldFocused ? 3 : 1}
                onFocus={() => {
                  setIsFieldFocused(true);
                }}
                eventContext="Shipment Dashboard - Quotes"
              />
            </div>
            {isFieldFocused && (
              <div className="d-flex flex-column align-items-between h-100">
                <div className="pt-3 d-flex justify-content-between">
                  <Button
                    variant="outline-primary"
                    label={intl.formatMessage({
                      id: "general.cancel",
                      defaultMessage: "Cancel",
                    })}
                    onClick={() => setIsFieldFocused(false)}
                  />
                  <Button
                    variant="primary"
                    type="submit"
                    label={intl.formatMessage({
                      id: "billing.postComment",
                      defaultMessage: "Post comment",
                    })}
                  />
                </div>
              </div>
            )}
          </Card.Body>
        </form>
      </FormProvider>
    </CardNew>
  );
};

Comments.propTypes = {
  invoiceId: PropTypes.string,
};

Comments.defaultProps = {
  invoiceId: undefined,
};

export default Comments;
